import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const CanjesMateriales = ({ user }) => {
  const API = `${window.location.origin}/api`;
  const [planes, setPlanes] = useState([]);
  const [saldoActual, setSaldoActual] = useState(0);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [activeTab, setActiveTab] = useState('disponibles');

  useEffect(() => {
    loadPlanesDisponibles();
    loadHistorial();
  }, []);

  const loadPlanesDisponibles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/canjes/planes-disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrar solo planes de categoría "Material"
      const planesMateriales = (response.data.planes || []).filter(
        plan => plan.categoria === 'Material'
      );
      setPlanes(planesMateriales);
      setSaldoActual(response.data.saldo_actual || 0);
    } catch (error) {
      console.error('Error loading planes:', error);
      toast.error('Error al cargar materiales digitales');
    } finally {
      setLoading(false);
    }
  };

  const loadHistorial = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/canjes/mi-historial`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrar solo canjes de materiales
      const canjesMateriales = (response.data.canjes || []).filter(
        canje => canje.plan_categoria === 'Material'
      );
      setHistorial(canjesMateriales);
    } catch (error) {
      console.error('Error loading historial:', error);
    }
  };

  const openConfirmModal = async (plan) => {
    setPlanSeleccionado(plan);
    setShowConfirmModal(true);
  };

  const handleCanjear = async () => {
    if (!planSeleccionado) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/canjes/canjear`,
        {
          plan_id: planSeleccionado.id,
          clase_seleccionada_id: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message, {
        autoClose: 5000,
        style: {
          background: 'linear-gradient(to right, #10b981, #3b82f6)',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold'
        }
      });

      setShowConfirmModal(false);
      setPlanSeleccionado(null);
      loadPlanesDisponibles();
      loadHistorial();
    } catch (error) {
      console.error('Error canjeando:', error);
      toast.error(error.response?.data?.detail || 'Error al realizar canje');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-UY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
      {/* Header con breadcrumb */}
      <div className="mb-6">
        <a href="/app/mis-denarios" className="text-green-600 hover:text-green-800 text-sm mb-2 inline-block">
          ← Volver a Mis Denarios
        </a>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center">
          <span className="text-3xl sm:text-4xl mr-3">📖</span>
          Material Digital
        </h2>
        
        {/* Saldo Card */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 shadow-lg mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium mb-1">Tu Saldo Actual</p>
              <p className="text-white text-4xl font-bold">{saldoActual} ₯</p>
            </div>
            <div className="text-6xl">🪙</div>
          </div>
        </div>

        <p className="text-sm sm:text-base text-gray-600">
          Canjea tus denarios por eBooks, PDFs, cursos y otros recursos digitales descargables
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('disponibles')}
          className={`px-4 py-2 font-semibold transition-all ${
            activeTab === 'disponibles'
              ? 'text-green-600 border-b-2 border-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🎁 Disponibles
        </button>
        <button
          onClick={() => setActiveTab('historial')}
          className={`px-4 py-2 font-semibold transition-all ${
            activeTab === 'historial'
              ? 'text-green-600 border-b-2 border-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📜 Mis Materiales ({historial.length})
        </button>
      </div>

      {/* Tab: Planes Disponibles */}
      {activeTab === 'disponibles' && (
        <>
          {loading ? (
            <div className="text-center py-12">
              <div className="text-xl text-gray-600">Cargando materiales...</div>
            </div>
          ) : planes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📖</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay materiales digitales disponibles
              </h3>
              <p className="text-gray-500">
                Pronto habrá nuevos recursos digitales para canjear
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {planes.map(plan => (
                <div
                  key={plan.id}
                  className={`border-2 rounded-2xl p-6 transition-all hover:shadow-xl ${
                    plan.puede_canjear
                      ? 'border-green-200 bg-gradient-to-br from-white to-green-50'
                      : 'border-gray-200 bg-gray-50 opacity-75'
                  }`}
                >
                  <div className="text-center mb-4">
                    <div className="text-6xl mb-2">{plan.icono}</div>
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold border bg-green-100 text-green-700 border-green-200">
                      Material Digital
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
                    {plan.nombre}
                  </h3>

                  <p className="text-sm text-gray-600 mb-4 text-center min-h-[40px]">
                    {plan.descripcion}
                  </p>

                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
                    <p className="text-xs text-gray-600 font-semibold mb-1">✨ Contenido:</p>
                    <p className="text-sm text-gray-800 font-medium">{plan.beneficio}</p>
                  </div>

                  <div className="bg-white rounded-xl p-4 mb-4 shadow-inner">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Costo</p>
                      <p className="text-3xl font-bold text-green-600">{plan.costo_denarios} ₯</p>
                    </div>
                  </div>

                  <button
                    onClick={() => openConfirmModal(plan)}
                    disabled={!plan.puede_canjear}
                    className={`w-full py-3 rounded-xl font-bold transition-all ${
                      plan.puede_canjear
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg hover:scale-105'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {plan.puede_canjear ? '📥 Canjear y Descargar' : '❌ Saldo Insuficiente'}
                  </button>

                  {!plan.puede_canjear && (
                    <p className="text-xs text-center text-red-600 mt-2">
                      Te faltan {plan.costo_denarios - saldoActual} ₯
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Tab: Historial */}
      {activeTab === 'historial' && (
        <>
          {historial.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📜</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Sin materiales canjeados
              </h3>
              <p className="text-gray-500">
                Tus materiales digitales aparecerán aquí con los links de descarga
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {historial.map(canje => (
                <div
                  key={canje.id}
                  className="border-2 border-green-200 rounded-xl p-6 hover:shadow-md transition-all bg-green-50"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-1">
                        {canje.plan_nombre}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">{canje.plan_beneficio}</p>
                      {canje.download_url && (
                        <a 
                          href={canje.download_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all text-sm font-semibold mt-2"
                        >
                          📥 Descargar Material
                          <i className="fas fa-external-link-alt text-xs"></i>
                        </a>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600 mb-1">
                        -{canje.costo_denarios} ₯
                      </p>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        {canje.estado}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Canjeado el {formatFecha(canje.fecha_canje)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal de Confirmación */}
      {showConfirmModal && planSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 rounded-t-2xl">
              <h3 className="text-2xl font-bold text-white text-center">
                📥 Confirmar Canje
              </h3>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{planSeleccionado.icono}</div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">
                  {planSeleccionado.nombre}
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {planSeleccionado.descripcion}
                </p>
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-800 font-medium">
                    📖 {planSeleccionado.beneficio}
                  </p>
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3">
                  <p className="text-xs text-blue-700">
                    💡 Después del canje, recibirás el link de descarga en tu historial
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Costo del canje:</span>
                  <span className="text-xl font-bold text-red-600">
                    -{planSeleccionado.costo_denarios} ₯
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Saldo actual:</span>
                  <span className="text-xl font-bold text-gray-800">{saldoActual} ₯</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800 font-semibold">Saldo después:</span>
                    <span className="text-2xl font-bold text-green-600">
                      {saldoActual - planSeleccionado.costo_denarios} ₯
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPlanSeleccionado(null);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCanjear}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Confirmar Canje
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CanjesMateriales;
